// Inline formatting a contentEditable legitimately produces. Anything else is unwrapped (its text kept) rather than dropped, so a user's words can never be deleted by sanitising.
const ALLOWED_TAGS = new Set(['SPAN', 'B', 'STRONG', 'I', 'EM', 'U', 'BR', 'DIV', 'P']);

// Presentation only — no positioning, sizing, background or url() payloads.
const ALLOWED_STYLE_PROPS = new Set(['font-weight', 'font-style', 'color', 'text-decoration']);

const filterStyle = (style: string | null): string => {
	if (!style) return '';

	return style
		.split(';')
		.map((declaration) => declaration.trim())
		.filter((declaration) => {
			const [prop, ...valueParts] = declaration.split(':');
			const value = valueParts.join(':').toLowerCase();
			if (!ALLOWED_STYLE_PROPS.has(prop?.trim().toLowerCase() ?? '')) return false;
			return !value.includes('url(') && !value.includes('expression(');
		})
		.join('; ');
};

const scrubElement = (element: Element): void => {
	// Snapshot before mutating — unwrapping splices a child's contents into this level.
	Array.from(element.children).forEach((child) => {
		scrubElement(child);

		if (!ALLOWED_TAGS.has(child.tagName)) {
			child.replaceWith(...Array.from(child.childNodes));
			return;
		}

		const safeStyle = filterStyle(child.getAttribute('style'));
		Array.from(child.attributes).forEach((attr) => child.removeAttribute(attr.name));
		if (safeStyle) child.setAttribute('style', safeStyle);
	});
};

/**
 * Whitelists the markup a blueprint text override may carry. Overrides hold innerHTML so an
 * edit preserves the generated bold/red styling instead of flattening it, which means the
 * stored string is rendered as HTML and must be scrubbed of scripts, handlers and urls first.
 */
export const sanitizeOverrideHtml = (html: string): string => {
	const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
	scrubElement(doc.body);
	return doc.body.innerHTML;
};

/**
 * The visible text of an override, with markup stripped and entities decoded. Required by any
 * caller that treats an override as DATA rather than rendering it — a naive digit scrape over the
 * raw value would read the 700 out of `style="font-weight:700"` as part of the number.
 */
export const overrideToPlainText = (value: string): string => {
	const doc = new DOMParser().parseFromString(`<body>${value}</body>`, 'text/html');
	return doc.body.textContent ?? '';
};
